/* PACKAGES */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

/* CONFIG */
import { IMAGE_URL } from "../../config";

/* UI COMPONENTS */
import { Button } from "react-bootstrap";

/* ICONS */
import FacebookIcon from "@iconscout/react-unicons/icons/uil-facebook";
import InstagramIcon from "@iconscout/react-unicons/icons/uil-instagram";

/* IMAGE IMPORTS */
import AmiKnox from "../../assets/agents/ami-knox.webp";

const Hiring = () => {
  /* UPDATE META DATA */
  const metaTitle = "Hiring | Amberwood Real Estate";
  const metaDescription =
    "When buying or selling a new home, it’s important that you find an experienced and local real estate agent that you can trust. Here at Amberwood, that’s exactly what you’ll find.";
  const metaImage =
    "https://storage.googleapis.com/cdn-amberwoodre/images/amberwood-og-image.png";

  document.title = metaTitle;
  document
    .querySelector('meta[name="description"]')
    .setAttribute("content", metaDescription);

  document
    .querySelector('meta[property="og:title"]')
    .setAttribute("content", metaTitle);
  document
    .querySelector('meta[property="og:description"]')
    .setAttribute("content", metaDescription);
  document
    .querySelector('meta[property="og:image"]')
    .setAttribute("content", metaImage);
  document
    .querySelector('meta[property="og:url"]')
    .setAttribute("content", window.location.href);

  document
    .querySelector('meta[name="twitter:title"]')
    .setAttribute("content", metaTitle);
  document
    .querySelector('meta[name="twitter:description"]')
    .setAttribute("content", metaDescription);
  document
    .querySelector('meta[name="twitter:image"]')
    .setAttribute("content", metaImage);

  return (
    <section className="hiring-form">
      {/* BANNER */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="page-banner"
      >
        <div className="heading-overlay">
          <div className="container">
            <div className="banner-hiring">
              <h1>WE ARE HIRING</h1>
              <div className="flex-cta">
                <img src={AmiKnox} alt="Ami Knox" />
                <div>
                  <h5 className="name">AMI H. KNOX, DIRECTOR OF OPERATIONS</h5>
                  <div className="contact">
                    <a href="tel:+15627559580" className="phone">
                      +1 562-755-9580
                    </a>
                    <a href="mailto:ami@amberwoodre.com" className="email">
                      ami@amberwoodre.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ABOUT COMPANY */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="about"
      >
        <div className="container">
          <div className="row align-items-center justify-content-between">
            <div className="col-xl-6 col-lg-12 col-sm-12">
              <div className="about-img">
                <img
                  src={IMAGE_URL + "images/we-have-a-common-goal.webp"}
                  style={{ marginBottom: "15px" }}
                  alt="About Amberwood Real Estate"
                />
              </div>
            </div>
            <div className="col-xl-6 col-lg-12 col-sm-12">
              <div className="about-details">
                <div>
                  <h2>WHO WE ARE</h2>
                  <p>
                    At Amberwood, we not only take real estate seriously, but we
                    also take agent support seriously. With us, you will find
                    strong mentorship, solid systems, and ample backend
                    assistance, so you will have more time to nurture
                    relationships and grow your business. We’re not just handing
                    you a login and wishing you luck. We’re involved, available,
                    and invested in helping you build something steady.
                  </p>
                  <p>
                    This isn’t about capturing as many recruits as possible, and
                    we definitely do not subscribe to the “everyone is for us”
                    theory. We are selective about who joins the team, and we do
                    so because we prioritize agents who are committed to the
                    craft of real estate and excel in it. If that describes you,
                    then you're in for a supportive team, clear guidelines, and
                    the ability to grow without the common office pressure. Our
                    goal is to support agents who are in it for the long haul.
                  </p>
                </div>
                <div className="social-media">
                  <div className="d-flex align-items-center">
                    <a
                      href="https://www.facebook.com/amberwoodre"
                      target="_blank"
                      style={{ textDecoration: "none" }}
                      rel="noreferrer"
                    >
                      <Button
                        className="social-media-btn"
                        aria-label="Amberwood Real Estate Facebook Handle"
                      >
                        <FacebookIcon color="#323232" size="20" />
                      </Button>
                    </a>
                    <a
                      href="https://www.instagram.com/amberwoodreinc/"
                      target="_blank"
                      style={{ textDecoration: "none" }}
                      rel="noreferrer"
                    >
                      <Button
                        className="social-media-btn"
                        aria-label="Amberwood Real Estate Instagram Handle"
                      >
                        <InstagramIcon color="#323232" size="20" />
                      </Button>
                    </a>
                  </div>
                  <Link to="/about-us" className="learn-more-link">
                    LEARN MORE
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* FINANCIAL BENEFITS */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="financial-benefits"
      >
        <div className="container">
          <h2>FINANCIAL BENEFITS AT AMBERWOOD</h2>
          <div className="row align-items-center justify-content-center">
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="benefit-box">
                <div className="round-number">01</div>
                <div>
                  <h5>No Start-Up Fees</h5>
                  <p>Begin your journey with zero initial costs.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="benefit-box">
                <div className="round-number">02</div>
                <div>
                  <h5>No Franchise Fees</h5>
                  <p>Keep 100% of your earnings, no franchise cuts.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="benefit-box">
                <div className="round-number">03</div>
                <div>
                  <h5>No Monthly/Desk Fees</h5>
                  <p>No recurring charges for desk or office use.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="benefit-box">
                <div className="round-number">04</div>
                <div>
                  <h5>No Copy/Scan/Print Fees</h5>
                  <p>Print, copy, and scan without extra charges.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="benefit-box">
                <div className="round-number">05</div>
                <div>
                  <h5>No E&O Insurance Fees</h5>
                  <p>We cover your Errors & Omissions insurance fully.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="benefit-box">
                <div className="round-number">06</div>
                <div>
                  <h5>No HIDDEN Fees</h5>
                  <p>Transparent pricing with absolutely no surprise fees.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* TOP REASONS TO JOIN AMBERWOOD */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="top-reasons"
      >
        <div className="container">
          <h2>TOP REASON TO JOIN AMBERWOOD</h2>
          <div className="row align-items-center justify-content-center">
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/commission.png")}
                  alt="Competitive Commission Split"
                />
                <div className="reason-text">
                  <h5>
                    Competitive Commission
                    <br />
                    Split
                  </h5>
                  <p>Maximize earnings with a generous commission structure.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/on-day-comission.png")}
                  alt="Agents Receive Commission On Day of Closing Escrow"
                />
                <div className="reason-text">
                  <h5>Agents Receive Commission On Day of Closing Escrow</h5>
                  <p>Get paid immediately when your deal closes.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/full-time-staff.png")}
                  alt="Full Time Staff - On Site Broker and Agent Advisor"
                />
                <div className="reason-text">
                  <h5>Full Time Staff - On Site Broker and Agent Advisor</h5>
                  <p>
                    Daily support from experienced broker and agent advisor.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/transaction-services.png")}
                  alt="Transaction Coordinating Services"
                />
                <div className="reason-text">
                  <h5>
                    Transaction Coordinating
                    <br />
                    Services
                  </h5>
                  <p>Let our team handle your paperwork efficiently.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/business-cards.png")}
                  alt="Free Business Cards and Marketing Materials"
                />
                <div className="reason-text">
                  <h5>Free Business Cards and Marketing Materials</h5>
                  <p>Promote yourself with free branding essentials.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/sales-meeting.png")}
                  alt="Weekly Sales Meeting and Education/Training Classes"
                />
                <div className="reason-text">
                  <h5>Weekly Sales Meeting and Education/Training Classes</h5>
                  <p>Stay sharp with ongoing training and market insights.</p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/24-hours.png")}
                  alt="Weekly Sales Meeting and Education/Training Classes"
                />
                <div className="reason-text">
                  <h5>24 Hour Access to Office </h5>
                  <p>
                    Work anytime with full office including Conference Room,
                    Computer Stations and Lounge with Coffee Bar.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="reason-box">
                <img
                  src={require("../../assets/icons/desk.png")}
                  alt="Weekly Sales Meeting and Education/Training Classes"
                />
                <div className="reason-text">
                  <h5>Personal Office and Workstation </h5>
                  <p>Personal offices and workstations available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* OUR OFFICES */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="our-offices"
      >
        <div className="container">
          <h2>TOUR DE AMBERWOOD</h2>
          <div className="row mt-3 justify-content-center">
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src={require("../../assets/selected/office-new-01.png")}
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-02.png"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-05.webp"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-03.png"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src={require("../../assets/selected/office-new-02.png")}
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-04.webp"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/office-06.webp"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src={require("../../assets/selected/office-new-03.png")}
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="cta-hiring"
      >
        <div className="container">
          <div className="cta-box">
            <div>
              <h2 className="head">Ready To Join the team</h2>
              <p className="text">
                Contact us on the given details to join our team and excel in
                your real estate career.
              </p>
            </div>
            <div className="flex-cta">
              <div>
                <h5 className="name">AMI H. KNOX, DIRECTOR OF OPERATIONS</h5>
                <div className="contact">
                  <a href="tel:+15627559580" className="phone">
                    +1 562-755-9580
                  </a>
                  <a href="mailto:ami@amberwoodre.com" className="email">
                    ami@amberwoodre.com
                  </a>
                </div>
              </div>
              <img src={AmiKnox} alt="Ami Knox" />
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hiring;
